// SEM-4779 — route-level authority gate for tenant-management routes.
//
// RequireAuth answers "is there a session"; it deliberately reads no role
// (ADR-017 — the server is the trust boundary). That left /payroll mounting the
// full admin wizard for any authenticated employee: the rail hid the entry, but
// a typed URL rendered every control, and the only denial arrived at write time.
// This guard closes that by asking the SAME two questions the rail asks — the
// capability cell and the server-resolved scope tier — as a pathless layout
// route, so a caller who may not read the surface gets the denial instead of it.
//
// Still convenience, not security: the server re-checks every request. What it
// buys is that the surface stops being offered at all, so the next read query
// added behind it cannot quietly become a leak.

import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation } from "react-router";

import { ErrorState, Loading } from "@novemai/components";

import { menuEntryKeyForPath } from "@/components/menu-config";
import { useVisibleMenu } from "@/components/use-visible-menu";

type GateOutcome = "loading" | "failed" | "denied" | "allowed";

/**
 * The ONE place a route guard becomes something on screen, so every guard
 * denies with the same page. Default-closed: only `allowed` renders the route.
 *
 * A failed signal is still closed, but it is reported as a failure, not as a
 * denial: telling an authorized admin "you don't have access" because /v1/me
 * was down sends them to support over an outage.
 */
function GateOutlet({ outcome }: { readonly outcome: GateOutcome }): ReactElement {
  const { t } = useTranslation();
  if (outcome === "loading") return <Loading label={t("auth.loading")} rows={4} />;
  if (outcome === "failed") {
    return (
      <ErrorState title={t("auth.authority_error_title")} description={t("errors.fallback")} />
    );
  }
  if (outcome === "denied") {
    return (
      <ErrorState
        title={t("auth.forbidden_title")}
        description={t("errors.code.auth.scope_forbidden")}
      />
    );
  }
  return <Outlet />;
}

/**
 * The route half of a nav entry: opens only for a caller the rail shows the
 * entry to. Same filter, same answer, so "the menu hides it" and "the URL
 * refuses it" cannot drift apart, and a route needs no capability / tier pair
 * copied out of the manifest (E38: an employee typing /people, /settings,
 * /benefits or /pdks was handed the admin shell).
 *
 * The entry is found from the URL (longest manifest `href` that covers it), so
 * one guard can wrap a whole family of pages. A URL no entry covers is DENIED:
 * the guard only wraps routes that are a rail destination, and a mismatch is a
 * wiring mistake that must not quietly open the route.
 *
 * Routes reachable by people the menu never lists (a person card opened from a
 * mention) stay outside it.
 */
export function RequireMenuEntry(): ReactElement {
  const { pathname } = useLocation();
  const { visibleKeys, resolved, failed } = useVisibleMenu();

  if (failed) return <GateOutlet outcome="failed" />;
  if (!resolved) return <GateOutlet outcome="loading" />;
  const entry = menuEntryKeyForPath(pathname);
  return <GateOutlet outcome={entry !== undefined && visibleKeys.includes(entry) ? "allowed" : "denied"} />;
}
